import { useEffect, useState } from "react";
import { Control, FieldValues, useController } from "react-hook-form";
import {
  Flex,
  Field,
  HStack,
  Input,
  Popover,
  Portal,
  Text,
} from "@chakra-ui/react";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/es";

import { Calendar, Trash } from "lucide-react";

import { InputGroup } from "../ui/input-group";
import { AppColors } from "@/styles/colors";
import { LightMode } from "../ui/color-mode";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.locale("es");

interface MonthYearPickerProps {
  placeholder?: string;
  name: string;
  labelText?: string;
  control?: Control<FieldValues>;
  errors?: any;
  disablePortal?: boolean;
  height?: string;
  minHeight?: string;
  timezone?: string;
  required?: boolean;
  minYear?: number;
  maxYear?: number;
  maxMonthsAhead?: number;
  minMonthsFromNow?: number;
  clearable?: boolean;
  onChange?: (value: string | undefined) => void;
  onClear?: () => void;
  disabled?: boolean;
  maxW?: string;
  showErrorMessage?: boolean;
}

// El valor se guarda como "YYYY-MM-01" para mantener compatibilidad con DatePicker
const MonthYearPicker = ({
  placeholder = "Selecciona mes y año",
  name,
  labelText,
  control,
  errors,
  disablePortal = false,
  required,
  height,
  minHeight,
  minYear,
  clearable = true,
  maxYear,
  maxMonthsAhead = 12,
  minMonthsFromNow = 0,
  timezone = "America/Argentina/Buenos_Aires",
  onChange,
  onClear,
  maxW,
  disabled,
  showErrorMessage = true,
}: MonthYearPickerProps) => {
  const { field } = useController({
    name,
    control,
  });

  const [isOpen, setIsOpen] = useState(false);

  const getDateInTimezone = (value?: string | Dayjs) => {
    if (!value) return dayjs().tz(timezone);
    return dayjs(value).tz(timezone);
  };

  const [selectedMonth, setSelectedMonth] = useState<number | undefined>(
    undefined,
  );
  const [selectedYear, setSelectedYear] = useState<number | undefined>(
    undefined,
  );

  const nowTz = getDateInTimezone();
  const currentYear = nowTz.year();
  const minAllowedDate = nowTz.add(minMonthsFromNow, "month").startOf("month");
  const maxAllowedDate = nowTz.add(maxMonthsAhead, "month");

  const effectiveMinYear = Math.max(
    minYear ?? currentYear - 100,
    minAllowedDate.year(),
  );
  const effectiveMaxYear = Math.min(
    maxYear ?? maxAllowedDate.year(),
    maxAllowedDate.year(),
  );

  const min = effectiveMinYear;
  const max = effectiveMaxYear;

  const [viewYear, setViewYear] = useState<number>(currentYear);

  useEffect(() => {
    if (!field.value) {
      setSelectedMonth(undefined);
      setSelectedYear(undefined);
      setViewYear(currentYear);
      return;
    }

    const valueStr = String(field.value);
    const [monthPart, yearPart] = valueStr.split(" ");
    const yearNum = Number(yearPart);

    if (!monthPart || Number.isNaN(yearNum)) {
      return;
    }

    const monthIndex = Array.from({ length: 12 }).findIndex((_, idx) => {
      const shortLabel = dayjs()
        .month(idx)
        .format("MMM")
        .replace(/^./, (ch) => ch.toUpperCase())
        .replace(".", "");

      return shortLabel.toLowerCase() === monthPart.slice(0, 3).toLowerCase();
    });

    if (monthIndex === -1) return;

    setSelectedMonth(monthIndex);
    setSelectedYear(yearNum);
    setViewYear(yearNum);
  }, [field.value, currentYear]);

  const applySelection = () => {
    if (selectedMonth === undefined || selectedYear === undefined) return;

    const formatted = dayjs()
      .year(selectedYear)
      .month(selectedMonth)
      .format("MMMM YYYY")
      .replace(/^./, (ch) => ch.toUpperCase());

    field.onChange(formatted);
    onChange?.(formatted);
    setIsOpen(false);
  };

  const displayValue = typeof field.value === "string" ? field.value : "";

const getMonthColor = (isSelected: boolean, isDisabled: boolean) => {
  if (isSelected) return "white";
  if (isDisabled) return AppColors.LIGHT_GRAY_TEXT;
  return AppColors.GRAY_TEXT;
};

const getMonthBgHover = (isSelected: boolean, isDisabled: boolean) => {
  if (isDisabled) return AppColors.WHITE_SECONDARY;
  if (isSelected) return AppColors.GREEN_PRIMARY;
  return AppColors.WHITE_BORDER_1;
};


  return (
    <LightMode>
      <Popover.Root
        open={isOpen}
        positioning={{ placement: "bottom" }}
        onOpenChange={(e) => setIsOpen(e.open)}
      >
        <Popover.Trigger asChild>
          <Field.Root invalid={!!errors}>
            {labelText && (
              <Field.Label>
                <Text fontWeight={600}>{labelText}</Text>
                {required && (
                  <Text fontWeight={600} color={AppColors.REQUIRED}>
                    *
                  </Text>
                )}
              </Field.Label>
            )}

            <InputGroup
              maxW={maxW}
              width="100%"
              flex="1"
              startElement={
                <Calendar
                  size="15px"
                  style={{ marginLeft: "12px", marginTop: "2px" }}
                />
              }
              endElement={
                field.value &&
                clearable && (
                  <Trash
                    data-testid="month-year-trash-button"
                    cursor={"pointer"}
                    size="15px"
                    style={{ marginRight: "10px" }}
                    onClick={(e) => {
                      field.onChange("");
                      onClear?.();
                      e.stopPropagation();
                    }}
                  />
                )
              }
              _placeholder={{
                color: AppColors.LIGHT_GRAY_TEXT,
              }}
            >
              <Input
                readOnly
                value={displayValue}
                placeholder={placeholder}
                onClick={() => setIsOpen(!isOpen)}
                cursor="pointer"
                height={height}
                minHeight={minHeight}
                data-testid="month-year-picker-input"
                disabled={disabled}
                _disabled={{
                  cursor: "not-allowed",
                }}
              />
            </InputGroup>
            {showErrorMessage && (
              <Field.ErrorText>
                <Text
                  css={{
                    "@media (max-width: 1350px) and (min-width: 1272px)": {
                      fontSize: "12px",
                    },
                    "@media (max-width: 1590px) and (min-width: 1351px)": {
                      fontSize: "14px",
                    },
                    "@media (min-width: 1591px)": {
                      fontSize: "16px",
                    },
                  }}
                >
                  {errors?.message}
                </Text>
              </Field.ErrorText>
            )}
          </Field.Root>
        </Popover.Trigger>
        <Portal disabled={disablePortal}>
          <Popover.Positioner zIndex={1500}>
            <LightMode>
              <Popover.Content
                bg="white"
                borderRadius="12px"
                boxShadow="lg"
                border={`1px solid ${AppColors.WHITE_BORDER_1}`}
                style={{ backgroundColor: "#FFFFFF" }}
              >
                <Flex
                  p={4}
                  minW="260px"
                  flexDir="column"
                  gap={4}
                  bg="white"
                  borderRadius="12px"
                >
                  <Flex justify="space-between" align="center">
                    <Text
                      as="button"
                      fontSize="lg"
                      fontWeight="medium"
                      cursor="pointer"
                      onClick={() =>
                        setViewYear((prev) => (prev <= min ? min : prev - 1))
                      }
                    >
                      {"<"}
                    </Text>
                    <Text fontSize="md" fontWeight={600}>
                      {viewYear}
                    </Text>
                    <Text
                      as="button"
                      fontSize="lg"
                      fontWeight="medium"
                      cursor="pointer"
                      onClick={() =>
                        setViewYear((prev) => (prev >= max ? max : prev + 1))
                      }
                    >
                      {">"}
                    </Text>
                  </Flex>

                  <Flex
                    as="div"
                    display="grid"
                    gridTemplateColumns="repeat(4, 1fr)"
                    gap={2}
                  >
                    {Array.from({ length: 12 }).map((_, idx) => {
                      const label = dayjs()
                        .month(idx)
                        .format("MMM")
                        .replace(/^./, (ch) => ch.toUpperCase())
                        .replace(".", "");

                      const isSelected =
                        selectedMonth === idx && selectedYear === viewYear;

                      const monthDate = dayjs()
                        .year(viewYear)
                        .month(idx)
                        .endOf("month");

                      const isBeforeMin = monthDate.isBefore(minAllowedDate);
                      const isAfterMax = monthDate.isAfter(maxAllowedDate);
                      const isDisabled = isBeforeMin || isAfterMax;

                      return (
                        <Flex
                          key={`${idx}-${label}`}
                          as="button"
                          align="center"
                          justify="center"
                          width="100%"
                          height="40px"
                          borderRadius="8px"
                          fontSize="sm"
                          fontWeight="medium"
                          bg={
                            isSelected
                              ? AppColors.GREEN_PRIMARY
                              : AppColors.WHITE_SECONDARY
                          }
                          color={
                            getMonthColor(isSelected, isDisabled)
                          }
                          cursor={isDisabled ? "not-allowed" : "pointer"}
                          _hover={{
                            bg: getMonthBgHover(isSelected, isDisabled)
                          }}
                          onClick={() => {
                            if (isDisabled) return;
                            setSelectedMonth(idx);
                            setSelectedYear(viewYear);
                          }}
                        >
                          {label}
                        </Flex>
                      );
                    })}
                  </Flex>

                  <HStack justify="flex-end">
                    <Text
                      as="button"
                      fontSize="sm"
                      fontWeight="medium"
                      color={AppColors.GREEN_PRIMARY}
                      onClick={applySelection}
                      data-testid="month-year-apply-button"
                    >
                      Aplicar
                    </Text>
                  </HStack>
                </Flex>
              </Popover.Content>
            </LightMode>
          </Popover.Positioner>
        </Portal>
      </Popover.Root>
    </LightMode>
  );
};

export default MonthYearPicker;
